import { useCallback } from "react";
import { api } from "../../../lib/api";
import { useOrders } from "../../../contexts/OrderContext";
import type { Pedido } from "../../../contexts/OrderContext";
import { sendToTerminal } from "../../../lib/terminalLogger";

interface UseRefuseActionParams {
	selectedPedido: Pedido | null;
	selectedItem: any | null;
	setSelectedPedidoFrozen: (pedido: Pedido) => void;
}

interface RefusePayload {
	motivo: string;
	detalhes: string;
	observacao: string;
	imagens: string[];
}

export function useRefuseAction({
	selectedPedido,
	selectedItem,
	setSelectedPedidoFrozen,
}: UseRefuseActionParams) {
	const { fetchPedidos, selectPedido } = useOrders();

	const submitRecusa = useCallback(
		async ({ motivo, detalhes, observacao, imagens }: RefusePayload) => {
			if (!selectedPedido || !selectedItem) {
				throw new Error("Pedido ou item inválido para recusa.");
			}

			const pedidoAtual = selectedPedido as Pedido;
			const pedidoIdNumero = Number(pedidoAtual.orc_id);
			const itemIdNumero = Number(selectedItem.oit_id);

			if (!pedidoIdNumero || !itemIdNumero) {
				throw new Error("IDs de pedido ou item inválidos.");
			}

			const payload = {
				pedidoId: pedidoIdNumero,
				itemId: itemIdNumero,
				motivo,
				detalhes: detalhes?.trim?.() || "",
				observacao,
				imagens,
			};

			const payloadLog = {
				pedidoId: pedidoIdNumero,
				itemId: itemIdNumero,
				motivo,
				detalhes: detalhes?.trim?.() || "",
				observacao: observacao.trim(),
				imagensCount: imagens.length,
			};

			try {
				sendToTerminal("📤 [RECUSA] Enviando solicitação", {
					endpoint: "/producao/recusar",
					method: "POST",
				});
				sendToTerminal("📋 [DATA]:", payloadLog);
			} catch {}

			try {
				const response: any = await api("/producao/recusar", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				});

				try {
					sendToTerminal("✅ [RECUSA] Registrada", {
						endpoint: "/producao/recusar",
					});
					sendToTerminal("📋 [DATA]:", {
						request: payloadLog,
						response,
					});
				} catch {}
			} catch (error) {
				try {
					sendToTerminal("❌ [RECUSA] Falha ao registrar", {
						endpoint: "/producao/recusar",
					});
					sendToTerminal("📋 [DATA]:", {
						request: payloadLog,
						error: error instanceof Error ? error.message : String(error),
					});
				} catch {}
				throw error instanceof Error
					? error
					: new Error("Erro ao registrar recusa.");
			}

			const novoStatusId = 10;
			const itensAtualizados = Array.isArray(pedidoAtual.itens)
				? pedidoAtual.itens.map((it: any) => {
						if (String(it.oit_id) === String(itemIdNumero)) {
							const statusAtualizado = {
								...(it.status ?? {}),
								oit_status_id: novoStatusId,
								descricao: "Recusa",
							};
							return {
								...it,
								status: statusAtualizado,
								__etapa_producao__: novoStatusId,
							};
						}
						return it;
					})
				: pedidoAtual.itens;

			const pedidoAtualizado: Pedido = {
				...pedidoAtual,
				itens: itensAtualizados,
			};

			setSelectedPedidoFrozen(pedidoAtualizado);
			selectPedido(pedidoAtualizado);

			try {
				await fetchPedidos(true); // ✅ Força refresh após recusa para garantir dados atualizados
			} catch (err) {
				try {
					sendToTerminal("⚠️ [RECUSA] Falha ao atualizar lista após recusa", {
						message: err instanceof Error ? err.message : String(err),
					});
				} catch {}
			}
		},
		[
			fetchPedidos,
			selectPedido,
			selectedItem,
			selectedPedido,
			setSelectedPedidoFrozen,
		],
	);

	return { submitRecusa };
}

export type UseRefuseActionResult = ReturnType<typeof useRefuseAction>;
